import {
  percent,
  ratio,
  toneOf,
  type DashboardOverview,
  type DashboardLimits,
  type DayRange,
  type Tone,
} from "@estelle/domain";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Info } from "lucide-react";
import type { ReactNode } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/modules/gst/format";
import {
  LOW_SAMPLE,
  durationDelta,
  durationLabel,
  percentLabel,
  pointDelta,
  relativeDelta,
  type Delta,
} from "@/modules/dashboard/format";
import { reportLink, type ReportGroup } from "@/modules/dashboard/links";
import { metricNote, type MetricNote } from "@/modules/dashboard/notes";

/**
 * How are we doing (#528): four groups of numbers for a period, each against
 * the period before, each opening the report for the same dates.
 *
 * A group is drawn only when the server sent it, which it does only to someone
 * who may open the report it links to. Every rate says how many shipments or
 * orders it is over, and below `LOW_SAMPLE` it is drawn grey and says so: a
 * rate from 6 parcels must not be read like one from 600.
 */

const DELTA_TONE: Record<Delta["tone"], string> = {
  good: "text-tint-green-foreground",
  bad: "text-destructive",
  neutral: "text-muted-foreground",
};

const FLAG_PILL: Record<Exclude<Tone, "ok">, string> = {
  amber: "bg-tint-orange text-tint-orange-foreground",
  red: "bg-tint-red text-tint-red-foreground",
};

interface MetricProps {
  label: string;
  /** The definition, always visible: a hover is no help on a phone. */
  hint: string;
  /** What it counts, how, and whether a value is healthy (#638). */
  note: MetricNote;
  value: string;
  delta: Delta | null;
  /** How many it is a rate or a median over, said under the label. */
  sample?: { count: number; unit: string };
  /** Over a limit: the figure says so in words as well as in colour. */
  flag?: { tone: Exclude<Tone, "ok">; text: string };
}

function Metric({
  label,
  hint,
  note,
  value,
  delta,
  sample,
  flag,
}: MetricProps) {
  const low = sample !== undefined && sample.count < LOW_SAMPLE;
  return (
    <div className="flex items-start justify-between gap-3 border-t pt-2.5 first:border-t-0 first:pt-0">
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <span className="text-sm font-medium">{label}</span>
          <MetricInfo label={label} note={note} />
        </div>
        <div className="text-muted-foreground text-[13px] leading-snug">
          {hint}
        </div>
        {sample ? (
          <div className="text-muted-foreground text-xs tabular-nums">
            {low ? "Low sample · " : ""}n = {String(sample.count)} {sample.unit}
          </div>
        ) : null}
      </div>
      <div className={cn("shrink-0 text-right", low && "opacity-60")}>
        <div className="text-xl leading-none font-semibold tabular-nums">
          {value}
        </div>
        {delta ? (
          <div
            className={cn("text-xs tabular-nums", DELTA_TONE[delta.tone])}
            title={delta.spoken}
          >
            {delta.text}
            <span className="sr-only"> ({delta.spoken})</span>
          </div>
        ) : null}
        {flag ? (
          <span
            className={cn(
              "mt-1 inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold",
              FLAG_PILL[flag.tone],
            )}
          >
            {flag.text}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The info button beside a metric (#638): what it counts, how, and whether
 * the value is healthy, in the words `metricNote` reads from the limits.
 * Above the card's own link, so opening it does not open the report.
 */
function MetricInfo({ label, note }: { label: string; note: MetricNote }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About ${label}`}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring/50 relative z-10 inline-flex size-6 items-center justify-center rounded-full outline-none focus-visible:ring-[3px] pointer-coarse:size-9"
        >
          <Info aria-hidden className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 text-sm">
        <p className="font-medium">{label}</p>
        <dl className="mt-2 grid gap-2">
          <div>
            <dt className="text-muted-foreground text-xs">What it counts</dt>
            <dd>{note.counts}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">How</dt>
            <dd>{note.how}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Is it healthy?</dt>
            <dd>{note.healthy}</dd>
          </div>
        </dl>
      </PopoverContent>
    </Popover>
  );
}

function Group({
  title,
  opens,
  group,
  range,
  children,
}: {
  title: string;
  opens: string;
  group: ReportGroup;
  range: DayRange;
  children: ReactNode;
}) {
  const link = reportLink(group, range);
  // The whole card still opens the report (#528): the link is stretched over
  // it, and the info buttons sit above the stretch so they open their note.
  return (
    <div className="bg-card text-card-foreground has-[a:focus-visible]:ring-ring/50 relative flex min-w-0 flex-col gap-3 rounded-md border p-3 hover:border-primary has-[a:focus-visible]:ring-[3px]">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        <Link
          to={link.to}
          search={link.search}
          className="text-muted-foreground inline-flex items-center gap-1 text-xs outline-none after:absolute after:inset-0 after:rounded-md"
          aria-label={`${title}. Opens ${opens} for the same dates.`}
        >
          {opens}
          <ArrowRight aria-hidden className="size-3" />
        </Link>
      </div>
      {children}
    </div>
  );
}

/** A rate flagged against its limits, only once it is over a sample worth trusting. */
function rateFlag(
  value: number | null,
  limit: DashboardLimits["rto_rate"],
  sample: number,
): MetricProps["flag"] {
  if (value === null || sample < LOW_SAMPLE) return undefined;
  const tone = toneOf(value, limit);
  if (tone === "ok") return undefined;
  return tone === "red"
    ? { tone, text: `Over ${String(limit.red)}%` }
    : { tone, text: `Over ${String(limit.amber)}%` };
}

export function Performance({ overview }: { overview: DashboardOverview }) {
  const { limits, sales, delivery, mix, speed } = overview;
  const range = overview.window.current;

  const rate = (part: number, whole: number) => percent(ratio(part, whole));

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-4">
      {sales ? (
        <Group
          title="Sales"
          opens="Reports → Sales"
          group="sales"
          range={range}
        >
          <Metric
            label="Orders"
            note={metricNote("orders", limits)}
            hint="Placed in the period, cancelled ones included."
            value={String(sales.current.orders)}
            delta={relativeDelta(
              sales.current.orders,
              sales.previous.orders,
              true,
            )}
          />
          {sales.current.revenue !== null ? (
            <Metric
              label="Revenue"
              note={metricNote("revenue", limits)}
              hint="Total of those orders as placed."
              value={formatMoney(sales.current.revenue)}
              delta={relativeDelta(
                Number(sales.current.revenue),
                sales.previous.revenue === null
                  ? null
                  : Number(sales.previous.revenue),
                true,
              )}
            />
          ) : null}
          {sales.current.averageOrderValue !== null ? (
            <Metric
              label="Average order value"
              note={metricNote("average_order_value", limits)}
              hint="Revenue divided by orders."
              value={formatMoney(sales.current.averageOrderValue)}
              delta={relativeDelta(
                Number(sales.current.averageOrderValue),
                sales.previous.averageOrderValue === null
                  ? null
                  : Number(sales.previous.averageOrderValue),
                true,
              )}
            />
          ) : null}
        </Group>
      ) : null}

      {delivery ? (
        <Group
          title="Delivery"
          opens="Reports → Delivery"
          group="delivery"
          range={range}
        >
          {(() => {
            const now = delivery.current;
            const before = delivery.previous;
            const rto = rate(now.rto, now.outcomes);
            const success = rate(now.delivered, now.outcomes);
            const first = rate(now.deliveredFirstAttempt, now.delivered);
            return (
              <>
                <Metric
                  label="RTO rate"
                  note={metricNote("rto_rate", limits)}
                  hint="Shipments that ended as RTO ÷ shipments delivered or RTO."
                  value={percentLabel(rto)}
                  delta={pointDelta(
                    rto,
                    rate(before.rto, before.outcomes),
                    false,
                  )}
                  sample={{ count: now.outcomes, unit: "shipments" }}
                  flag={rateFlag(rto, limits.rto_rate, now.outcomes)}
                />
                <Metric
                  label="Delivery success"
                  note={metricNote("delivery_success", limits)}
                  hint="The remainder: delivered ÷ delivered or RTO."
                  value={percentLabel(success)}
                  delta={pointDelta(
                    success,
                    rate(before.delivered, before.outcomes),
                    true,
                  )}
                  sample={{ count: now.outcomes, unit: "shipments" }}
                />
                <Metric
                  label="First-attempt delivery"
                  note={metricNote("first_attempt", limits)}
                  hint="Delivered on the first attempt ÷ delivered."
                  value={percentLabel(first)}
                  delta={pointDelta(
                    first,
                    rate(before.deliveredFirstAttempt, before.delivered),
                    true,
                  )}
                  sample={{ count: now.delivered, unit: "delivered" }}
                />
              </>
            );
          })()}
        </Group>
      ) : null}

      {mix ? (
        <Group title="Mix" opens="Reports → Orders" group="mix" range={range}>
          {(() => {
            const now = mix.current;
            const before = mix.previous;
            const paid = now.cod + now.prepaid;
            const codShare = rate(now.cod, paid);
            const cancelled = rate(now.cancelled, now.placed);
            return (
              <>
                <Metric
                  label="COD / prepaid"
                  note={metricNote("cod_share", limits)}
                  hint="Share of orders paid on delivery and paid up front."
                  value={
                    codShare === null
                      ? "–"
                      : `${percentLabel(codShare)} / ${percentLabel(Math.round((100 - codShare) * 10) / 10)}`
                  }
                  delta={pointDelta(
                    codShare,
                    rate(before.cod, before.cod + before.prepaid),
                    null,
                  )}
                  sample={{ count: paid, unit: "orders" }}
                />
                <Metric
                  label="Cancellation rate"
                  note={metricNote("cancellation_rate", limits)}
                  hint="Cancelled ÷ placed."
                  value={percentLabel(cancelled)}
                  delta={pointDelta(
                    cancelled,
                    rate(before.cancelled, before.placed),
                    false,
                  )}
                  sample={{ count: now.placed, unit: "orders" }}
                  flag={rateFlag(
                    cancelled,
                    limits.cancellation_rate,
                    now.placed,
                  )}
                />
              </>
            );
          })()}
        </Group>
      ) : null}

      {speed ? (
        <Group
          title="Speed"
          opens="Reports → Delivery"
          group="speed"
          range={range}
        >
          <Metric
            label="Order to ship (median)"
            note={metricNote("order_to_ship", limits)}
            hint="Order placed to shipment leaving, for shipments that left."
            value={durationLabel(speed.current.orderToShipHours)}
            delta={durationDelta(
              speed.current.orderToShipHours,
              speed.previous.orderToShipHours,
            )}
            sample={{
              count: speed.current.orderToShipCount,
              unit: "shipments",
            }}
          />
          <Metric
            label="Ship to deliver (median)"
            note={metricNote("ship_to_deliver", limits)}
            hint="Shipment leaving to delivered, for shipments delivered."
            value={durationLabel(speed.current.shipToDeliverHours)}
            delta={durationDelta(
              speed.current.shipToDeliverHours,
              speed.previous.shipToDeliverHours,
            )}
            sample={{
              count: speed.current.shipToDeliverCount,
              unit: "shipments",
            }}
          />
        </Group>
      ) : null}
    </div>
  );
}
